import { StringContext } from "../../core/StringContext";
import { StringModule } from "../../core/StringModule";
import { StringObject } from "../../objects/StringObject";

/**
 * Module that handles lazy-loading of images with `string-lazy` attribute.
 * Automatically loads and applies aspect-ratio once image is loaded.
 */
export class StringLazy extends StringModule {
  private isStartLoaded = false;
  private loadingCount = 0;

  constructor(context: StringContext) {
    super(context);
    this.htmlKey = "lazy";
  }

  /**
   * Called on module start — preloads all <img string-lazy> in DOM.
   */
  onInit(): void {
    const images = document.querySelectorAll(
      "img[string-lazy], img[data-string-lazy]"
    );
    images.forEach((img) => this.loadImage(img as HTMLImageElement));
    this.isStartLoaded = true;
  }

  /**
   * Called when an image becomes a managed object.
   */
  onObjectConnected(object: StringObject): void {
    this.loadingCount++;

    if (this.isStartLoaded) {
      const img = object.htmlElement as HTMLImageElement;
      this.loadImage(img);
    }
  }

  /**
   * Loads image and sets aspect-ratio based on real dimensions.
   */
  private async loadImage(img: HTMLImageElement): Promise<void> {
    const src = this.tools.domAttribute.process({
      element: img,
      key: this.htmlKey,
      fallback: "",
    });

    if (!src) return;

    try {
      img.classList.add("lazyLoad");
      img.src = src;

      img.addEventListener("load", () => {
        img.classList.add("-loaded");
      });
      await this.setAspectRatio(img, src);
    } catch (err) {
      console.warn("Failed to load image:", src);
    }
  }

  /**
   * Loads image data to extract real dimensions and apply aspect-ratio style.
   */
  private setAspectRatio(el: HTMLElement, url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("GET", url, true);
      xhr.responseType = "arraybuffer";
      xhr.setRequestHeader("Range", "bytes=0-");
      xhr.onload = () => {
        if (xhr.status === 200 || xhr.status === 206) {
          const blob = new Blob([xhr.response]);
          const img = new Image();
          img.onload = () => {
            el.style.aspectRatio = `${img.width} / ${img.height}`;
            URL.revokeObjectURL(img.src);

            this.loadingCount--;
            if (this.loadingCount <= 0) {
              this.events.emit("image:load:all", null);
              this.loadingCount = 0;
            }

            resolve();
          };
          img.onerror = () => {
            URL.revokeObjectURL(img.src);
            this.loadingCount--;
            reject(new Error("Image failed to decode"));
          };
          img.src = URL.createObjectURL(blob);
        } else {
          reject(new Error("Image request failed"));
        }
      };
      xhr.onerror = () => reject(new Error("XHR error"));
      xhr.send();
    });
  }
}
